import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MessageSquare, CalendarCheck, ShieldCheck, Trees, LayoutGrid, Sun, Trophy,
  Baby, PersonStanding, Car, Leaf, Train, BadgeCheck, Award,
} from "lucide-react";
import { initLenis } from "../lib/smooth";
import { getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, AGENT_REG, WA_MESSAGE } from "../data/site";

const GATED_FACTORS = [
  { icon: ShieldCheck, title: "Security", desc: "CCTV in common areas, video door phones and controlled access are the baseline of a genuine gated community." },
  { icon: Trees, title: "Open & Landscaped Spaces", desc: "Central greens, gardens and tree cover shape air quality and daily wellbeing." },
  { icon: LayoutGrid, title: "Apartment Planning", desc: "Efficient layouts with minimal wasted circulation give you more usable home." },
  { icon: Sun, title: "Natural Light & Ventilation", desc: "Open-sided apartments with cross ventilation stay brighter and cooler." },
  { icon: Trophy, title: "Sports & Recreation", desc: "Courts, tracks and play arenas support an active routine without leaving home." },
  { icon: Baby, title: "Children's Spaces", desc: "Safe, dedicated play areas inside the gates matter for young families." },
  { icon: PersonStanding, title: "Senior-Friendly Facilities", desc: "Comfortable seating, gentle walking paths and calm corners for elders." },
  { icon: Car, title: "Parking & Access", desc: "Organised vehicle movement and resident parking reduce daily friction." },
  { icon: Leaf, title: "Sustainability", desc: "Green-building certification, rainwater harvesting and solar common lighting lower running costs." },
  { icon: Train, title: "Connectivity", desc: "Metro access and realistic commute times to work, schools and hospitals." },
  { icon: BadgeCheck, title: "RERA Verification", desc: "Verify the project and agent registration on the WBRERA website before booking." },
  { icon: Award, title: "Developer Track Record", desc: "Delivery history and resident satisfaction tell you what brochures cannot." },
];

const V21A_FEATURES = [
  "2 & 3 BHK residences across five towers",
  "90% homes with 3-side open views",
  "Double-height sky terraces",
  "Efficient floor plans with reduced circulation space",
  "Vastu-compliant layouts",
  "Natural light and cross ventilation",
  "IGBC Precertified Platinum Rated Green Building",
  "3-acre Central Park",
  "Theme Garden",
  "Open-air Futsal Ground",
  "Athletic Track",
  "Lawn Tennis Court",
  "Cricket Play Arena",
  "Pet Park",
  "Forest Trail",
  "Jogging Track",
  "Cycling Track",
  "Podium Garden",
  "CCTV surveillance in common areas",
  "Video Door Phone",
  "Shuttle service to nearest metro planned",
];

const GATED_FAQS = [
  {
    q: "What is a gated community property?",
    a: "A gated community is a residential development with controlled entry, shared security systems and common amenities maintained for residents — typically including surveillance in common areas, managed open spaces and recreational facilities within the boundary.",
  },
  {
    q: "What should I check before buying in a gated community?",
    a: "Check security provisions (CCTV, video door phone), the quality of open and landscaped spaces, apartment planning and carpet area, light and ventilation, sports and children's facilities, senior-friendly features, parking and access, sustainability measures, connectivity, RERA registration and the developer's track record.",
  },
  {
    q: "Which is the best gated property in New Town Kolkata?",
    a: "There is no single best gated project for every buyer — the choice depends on your budget, apartment size, location, amenities, the developer's credentials and your personal requirements. Buyers comparing options near Action Area III may find Vinayak 21 Acres worth considering for verified features like its 3-acre central park, 90% homes with 3-side open views, double-height sky terraces, IGBC Precertified Platinum rating, CCTV in common areas and video door phones.",
  },
  {
    q: "Are 2 BHK and 3 BHK gated properties available in New Town?",
    a: "Yes. Gated developments across New Town offer both configurations. At Vinayak 21 Acres near Action Area III, 2 BHK 2T homes and 3 BHK Smart, Comfort and Luxury layouts are available across five towers.",
  },
  {
    q: "What gated-community features does Vinayak 21 Acres offer?",
    a: "Verified features include CCTV surveillance in common areas, video door phones, a 3-acre central park, theme garden, sports facilities (futsal, lawn tennis, cricket, pickleball, badminton, basketball), jogging and cycling tracks, a forest trail, pet park, kids' and adventure play areas, senior citizens' seating and a planned metro shuttle.",
  },
  {
    q: "Where is Vinayak 21 Acres located?",
    a: "Vinayak 21 Acres is at Hatishala — off and adjacent to Newtown Action Area III, Kolkata.",
  },
  {
    q: "What amenities are planned at Vinayak 21 Acres?",
    a: "The amenity plan centres on the 3-acre central park with elevated cabanas, a grand lawn, play areas, an open-air amphitheatre, cascading waters and a maze garden, plus an athletic track, multiple courts, a barbecue nook, outdoor fitness zone and podium garden.",
  },
  {
    q: "How can I check the latest price and availability?",
    a: "Pricing and availability are subject to change. Contact Forest Hill Realtors through the enquiry buttons on this page, WhatsApp or a call for the latest developer-provided pricing and available inventory.",
  },
  {
    q: "How can I book a site visit?",
    a: "Use any Book Site Visit button on this page, the sticky enquiry bar on mobile, or message us on WhatsApp — Forest Hill Realtors will arrange a convenient slot.",
  },
];

export default function GatedCommunity() {
  const [modal, setModal] = useState({ open: false, source: "Gated Community Page Enquiry", interest: "" });
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });

  useEffect(() => {
    document.title = "Gated Community Properties in New Town Kolkata | Buyer Guide";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Looking for gated community properties in New Town Kolkata? Explore what buyers should consider and discover Vinayak 21 Acres near Newtown Action Area III."
      );
    initLenis();
    getSiteSettings().then(setContact);
  }, []);

  const openModal = (source, interest = "") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="bg-[#FFFFFF]" data-testid="gated-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="gated-overline">Buyer's Guide · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-4xl"
              data-testid="gated-h1"
            >
              Gated Community Properties in <span className="text-[#84CC16]">New Town Kolkata</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              A practical guide for buyers comparing gated properties in New Town — what makes a gated community worth
              living in, what to verify before booking, and one residential gated community near Newtown Action Area 3
              worth a closer look.
            </p>
            <div className="mt-8">
              <Link to="/" data-testid="gated-home-link"
                className="inline-flex items-center gap-2 text-[#84CC16] font-sub font-semibold text-sm border border-[#84CC16]/50 rounded-full px-6 py-3 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300">
                Explore Vinayak 21 Acres <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        <section data-testid="gated-intro-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="01" eyebrow="Start Here" title="Gated Community Properties in New Town Kolkata" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Choosing a gated community in New Town Kolkata is about more than a boundary wall and a security
                  cabin. The best-fit residential gated community in New Town Kolkata for your family depends on how
                  well the project handles security, open space, apartment planning and everyday convenience — and
                  whether its location works for your daily routine.
                </p>
                <p>
                  This guide breaks down what genuinely matters when comparing gated community flats in New Town
                  Kolkata, and introduces one option near Action Area III to shortlist.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-factors-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="02" eyebrow="The Checklist" title="What Makes a Good Gated Community?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {GATED_FACTORS.map((f, i) => (
                <Reveal key={f.title} delay={0.03 * i}>
                  <div data-testid={`gated-factor-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl p-6 h-full hover:border-[#84CC16] hover:-translate-y-1 hover:shadow-[0_20px_40px_-18px_rgba(77,124,15,0.18)] transition-all duration-500">
                    <div className="w-11 h-11 rounded-full bg-[#EAF5D6] border border-[#84CC16]/30 flex items-center justify-center text-[#365314]">
                      <f.icon size={19} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-serif-display text-xl text-[#365314] mt-4 leading-snug">{f.title}</h3>
                    <p className="text-sm text-[#65A30D] mt-2 leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="gated-location-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="03" eyebrow="Connectivity" title="Location & Connectivity" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  A gated community should reduce daily friction, not add to it. When comparing properties, map the
                  real travel times to workplaces, schools, hospitals, shopping and transport — at the hours you
                  actually travel. Proximity to employment hubs like Action Area III, metro connectivity and access to
                  daily conveniences often outweigh a marginal difference in apartment size.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-green-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="04" eyebrow="Outdoors" title="Green Spaces & Outdoor Lifestyle" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Central green areas are the lungs of a gated community. A large central park, walking and jogging
                  paths, and recreational courts give residents room to exercise, socialise and let children play
                  safely within the gates. When visiting any project, spend time in its open spaces — not just the
                  sample flat — and imagine an ordinary weekday evening there.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-amenities-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="05" eyebrow="Lifestyle" title="Amenities to Consider in a Gated Community" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Useful amenities tend to be the ones used weekly: play areas, jogging tracks, sports courts, pet
                  zones, senior seating and outdoor fitness corners. Longer lists — amphitheatres, barbecue nooks,
                  skating rinks — add variety, but not every project offers every facility, so verify the actual
                  amenity plan rather than relying on category averages.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-project-section" className="py-16 sm:py-24 bg-[#EAF5D6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="06" eyebrow="One Option Nearby" title="Vinayak 21 Acres – A Residential Option Near Newtown Action Area III" dark />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
                Among gated residential property in New Town's eastern corridor, Vinayak 21 Acres is one option buyers
                may consider. Vinayak 21 Acres is at Hatishala, off/adjacent to Newtown Action Area III, Kolkata.
                Based on the project's published information, its verified features include:
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-8">
              {V21A_FEATURES.map((f, i) => (
                <Reveal key={f} delay={0.02 * i}>
                  <div data-testid={`gated-feature-${i}`} className="flex items-start gap-3 border border-[#3F6212]/12 rounded-xl px-5 py-4 bg-[#3F6212]/[0.04]">
                    <span className="text-[#84CC16] mt-0.5">✦</span>
                    <p className="text-sm text-[#3F6212] leading-relaxed">{f}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="gated-options-section" className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="07" eyebrow="Configurations" title="2 BHK & 3 BHK Options" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
                The project offers 2 BHK 2T residences (approx. 595–597 sq ft carpet) and 3 BHK homes in Smart
                (approx. 698–703 sq ft carpet), Comfort (approx. 736–783 sq ft) and Luxury (approx. 823–943 sq ft)
                families across five towers. Explore the dedicated guides:
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/2-bhk-flats-new-town-kolkata" data-testid="gated-2bhk-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  2 BHK Flats in New Town Kolkata <ArrowRight size={15} />
                </Link>
                <Link to="/3-bhk-flats-new-town-kolkata" data-testid="gated-3bhk-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  3 BHK Flats in New Town Kolkata <ArrowRight size={15} />
                </Link>
                <button data-testid="gated-check-availability-btn" onClick={() => openModal("Availability Enquiry")}
                  className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                  Check Availability
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-price-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="08" eyebrow="Pricing" title="Price & Availability" />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-7 sm:p-10 mt-8">
                <p className="text-[#4D7C0F] text-base sm:text-lg leading-relaxed" data-testid="gated-price-note">
                  Pricing and availability are subject to change. Contact Forest Hill Realtors for the latest
                  developer-provided pricing and available inventory.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="gated-get-price-btn" onClick={() => openModal("Get Latest Price")}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Get Latest Price
                  </button>
                  <Link to="/vinayak-21-acres-price" data-testid="gated-price-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Vinayak 21 Acres Price <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-floorplans-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <SectionHeading index="09" eyebrow="Layouts" title="Floor Plans" center />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                View all 40 official floor plans — every tower and type — with carpet area, balcony, BUA and SBUA for
                each layout.
              </p>
              <div className="mt-8">
                <Link to="/vinayak-21-acres-floor-plan" data-testid="gated-floorplan-link"
                  className="inline-flex items-center gap-2 bg-[#84CC16] text-[#365314] font-sub font-bold text-sm px-8 py-4 rounded-full hover:bg-[#9ADB3E] active:scale-[0.98] transition-all duration-300">
                  Vinayak 21 Acres Floor Plans <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-fit-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="10" eyebrow="Decision" title="Is Vinayak 21 Acres Right for You?" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  It depends on your configuration requirement (2 BHK versus the three 3 BHK families), your budget
                  once all applicable charges are included, your location preference around Action Area III, how much
                  you value open green space and sports amenities, and whether the floor plans match how your
                  household actually lives.
                </p>
                <p>
                  We recommend a site visit before any decision — walk the central park, see the light and ventilation
                  in person, and compare the layout against your shortlist. No property suits everyone, and no outcome
                  can be guaranteed; the right choice is the one that fits your requirements.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-visit-section" className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <p className="overline-tag">11 · Visit</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight mt-4">
                  Book a Site Visit
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  Experience the gates, the greens and the homes in person before you compare your shortlist.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                  <button data-testid="gated-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                  <a data-testid="gated-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2">
                  <Link to="/best-property-in-new-town-kolkata" data-testid="gated-guide-link"
                    className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Best Property in New Town Kolkata Guide <ArrowRight size={14} />
                  </Link>
                  <Link to="/properties-in-new-town-action-area-3" data-testid="gated-aa3-link"
                    className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Properties in New Town Action Area 3 <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="gated-faq-section" className="pb-16 sm:pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="12" eyebrow="FAQs" title="Gated Community FAQs" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="gated-faq-accordion">
                {GATED_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`gated-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`gated-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="gated-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span>
            </p>
          </div>
        </div>

        <Footer />
      </main>
      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
